<div
  class="prs-all-drawer-backdrop"
  [class.is-open]="open$ | async"
  (click)="close()"
  role="presentation"
></div>
<aside
  #prsAllDrawerPanel
  class="prs-all-drawer"
  id="prs-all-drawer-panel"
  [class.is-open]="open$ | async"
  [class.is-drilldown]="hasSubpanel$ | async"
  role="dialog"
  tabindex="-1"
  [attr.aria-modal]="(open$ | async) ? true : null"
  [attr.aria-hidden]="(open$ | async) ? null : 'true'"
  [attr.inert]="(open$ | async) ? null : ''"
  [attr.aria-label]="'shell.all_menu' | translate"
  data-testing-id="prs-all-drawer"
>
  <div class="prs-all-drawer__head">
    <button
      #prsAllDrawerClose
      type="button"
      class="prs-shell-link-quiet text-white ms-auto"
      data-testing-id="prs-all-drawer-close"
      [attr.aria-label]="'dialog.close.text' | translate"
      (click)="close()"
    >
      {{ 'dialog.close.text' | translate }}
    </button>
  </div>

  <div class="prs-all-drawer__body">
    <!-- Amazon-style main menu: portal + shop + account, user scrolls -->
    <ng-container *ngIf="!(hasSubpanel$ | async)">
      <!-- FR2.2.1 — same portal destinations as desktop horizontal nav (shown when hamburger replaces that row).
           Full-width vertical list (same row chrome as shop categories) — not a wrapped chip row. -->
      <div class="prs-all-drawer__portal" data-testing-id="prs-all-drawer-portal">
        <p class="prs-all-drawer__section-label">{{ 'shell.portal.nav_aria' | translate }}</p>
        <ul class="prs-all-drawer__list">
          <li>
            <a
              class="prs-all-drawer__row"
              routerLink="/home"
              data-testing-id="prs-all-portal-overview"
              (click)="close()"
              >{{ 'shell.portal.nav.overview' | translate }}</a
            >
          </li>
          <li *ishIsAuthorizedTo="['APP_B2B_ORDER_APPROVAL', 'APP_B2B_MANAGE_COSTCENTER']">
            <a
              class="prs-all-drawer__row"
              routerLink="/account/requisitions/approver"
              data-testing-id="prs-all-portal-approvals"
              (click)="close()"
              >{{ 'shell.portal.nav.approvals' | translate }}</a
            >
          </li>
          <li *ngIf="isWarehouseCustomer$ | async">
            <a
              class="prs-all-drawer__row"
              routerLink="/category/WH"
              data-testing-id="prs-all-portal-warehouse"
              (click)="close()"
              >{{ 'shell.portal.nav.warehouse' | translate }}</a
            >
          </li>
          <li>
            <a
              class="prs-all-drawer__row"
              [routerLink]="['/c', catalogRootCategoryId]"
              data-testing-id="prs-all-portal-shop"
              (click)="close()"
              >{{ 'shell.portal.nav.shop' | translate }}</a
            >
          </li>
          <li>
            <a
              class="prs-all-drawer__row"
              routerLink="/account/quotes"
              data-testing-id="prs-all-portal-quotes"
              (click)="close()"
              >{{ 'shell.portal.nav.quotes' | translate }}</a
            >
          </li>
          <!-- Punchout (cXML/OCI): hide portal destinations punchout shoppers cannot use -->
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-all-drawer__row"
              routerLink="/account/orders"
              data-testing-id="prs-all-portal-orders"
              (click)="close()"
              >{{ 'shell.portal.nav.orders' | translate }}</a
            >
          </li>
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-all-drawer__row"
              routerLink="/account/invoices"
              data-testing-id="prs-all-portal-invoices"
              (click)="close()"
              >{{ 'shell.portal.nav.invoices' | translate }}</a
            >
          </li>
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-all-drawer__row"
              routerLink="/account/contracts"
              data-testing-id="prs-all-portal-contracts"
              (click)="close()"
              >{{ 'shell.portal.nav.contracts' | translate }}</a
            >
          </li>
          <li *ishHasNotRole="['APP_B2B_CXML_USER', 'APP_B2B_OCI_USER']">
            <a
              class="prs-all-drawer__row"
              routerLink="/account/reports"
              data-testing-id="prs-all-portal-reports"
              (click)="close()"
              >{{ 'shell.portal.nav.reports' | translate }}</a
            >
          </li>
          <li *ishIsAuthorizedTo="'APP_B2B_MANAGE_USERS'">
            <a
              class="prs-all-drawer__row"
              routerLink="/account/organization/users"
              data-testing-id="prs-all-portal-users"
              (click)="close()"
              >{{ 'shell.portal.nav.users' | translate }}</a
            >
          </li>
        </ul>
      </div>

      <hr class="prs-all-drawer__rule" />

      <ul class="prs-all-drawer__list">
        <li *ngFor="let category of categories$ | async">
          <button
            *ngIf="category.hasChildren; else leafCategoryLink"
            type="button"
            class="prs-all-drawer__row"
            (click)="focusCategory(category)"
          >
            <span>{{ category.name }}</span>
            <i class="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
          <ng-template #leafCategoryLink>
            <a class="prs-all-drawer__row" [routerLink]="category.url" (click)="close()">{{ category.name }}</a>
          </ng-template>
        </li>
      </ul>

      <hr class="prs-all-drawer__rule" />

      <ng-container *ngIf="isLoggedIn$ | async; else guestAccountLinks">
        <div class="prs-all-drawer__account">
          <p class="prs-all-drawer__section-label">{{ 'shell.your_account' | translate }}</p>
          <!-- Sales Accounts switcher lives on the Viewing account header strip — not duplicated here -->
          <div
            *ngIf="canImpersonate$ | async"
            class="prs-all-drawer__sales-rep"
            data-testing-id="all-drawer-csr-impersonation-picker"
          >
            <prs-impersonation-picker />
          </div>
          <ng-container *ngFor="let item of accountNavItems$ | async">
            <ng-container *ishIsAuthorizedTo="item.permission || 'always'">
              <ng-container *ishHasNotRole="item.notRole">
                <ng-container
                  *ngIf="(item.feature || 'always' | ishFeature) && (item.serverSetting || 'always' | ishServerSetting)"
                >
                  <a
                    *ngIf="!item.children?.length && item.routerLink"
                    class="prs-all-drawer__account-link prs-all-drawer__account-link--solo"
                    [routerLink]="item.routerLink"
                    [attr.data-testing-id]="'prs-all-' + item.id"
                    (click)="close()"
                  >
                    <span class="prs-all-drawer__account-icon" aria-hidden="true">
                      <i class="bi" [ngClass]="iconFor(item)"></i>
                    </span>
                    <span>{{ item.localizationKey | translate }}</span>
                  </a>
                  <section
                    *ngIf="item.children?.length || item.id === 'my-favorite'"
                    class="prs-all-drawer__account-group"
                  >
                    <h3 class="prs-all-drawer__account-heading">
                      <span class="prs-all-drawer__account-icon" aria-hidden="true">
                        <i class="bi" [ngClass]="iconFor(item)"></i>
                      </span>
                      <span>{{ item.localizationKey | translate }}</span>
                    </h3>
                    <div class="prs-all-drawer__account-links">
                      <ng-container *ngFor="let subItem of item.children">
                        <ng-container *ishIsAuthorizedTo="subItem.permission || 'always'">
                          <ng-container *ishHasNotRole="subItem.notRole">
                            <a
                              *ngIf="
                                (subItem.feature || 'always' | ishFeature) &&
                                (subItem.serverSetting || 'always' | ishServerSetting) &&
                                subItem.routerLink
                              "
                              class="prs-all-drawer__account-link"
                              [routerLink]="subItem.routerLink"
                              [attr.data-testing-id]="'prs-all-' + subItem.id"
                              (click)="close()"
                            >
                              <span class="prs-all-drawer__account-icon" aria-hidden="true">
                                <i class="bi" [ngClass]="iconFor(subItem)"></i>
                              </span>
                              <span>{{ subItem.localizationKey | translate }}</span>
                            </a>
                          </ng-container>
                        </ng-container>
                      </ng-container>
                      <ng-container *ngIf="item.id === 'my-favorite'">
                        <ng-container *ngFor="let block of favoriteBlocks$ | async">
                          <a
                            *ngIf="!block.external"
                            class="prs-all-drawer__account-link"
                            [routerLink]="block.url"
                            [attr.data-testing-id]="'prs-all-fav-block-' + block.id"
                            (click)="close()"
                          >
                            <span class="prs-all-drawer__account-icon" aria-hidden="true">
                              <i class="bi" [ngClass]="block.icon || 'bi-link-45deg'"></i>
                            </span>
                            <span>{{ block.name }}</span>
                          </a>
                          <a
                            *ngIf="block.external"
                            class="prs-all-drawer__account-link"
                            [href]="block.url"
                            rel="noopener noreferrer"
                            target="_blank"
                            [attr.data-testing-id]="'prs-all-fav-block-' + block.id"
                            (click)="close()"
                          >
                            <span class="prs-all-drawer__account-icon" aria-hidden="true">
                              <i class="bi" [ngClass]="block.icon || 'bi-link-45deg'"></i>
                            </span>
                            <span>{{ block.name }}</span>
                          </a>
                        </ng-container>
                      </ng-container>
                    </div>
                  </section>
                </ng-container>
              </ng-container>
            </ng-container>
          </ng-container>
        </div>
      </ng-container>

      <ng-template #guestAccountLinks>
        <div class="prs-all-drawer__account prs-all-drawer__account--guest">
          <p class="prs-all-drawer__section-label">{{ 'shell.your_account' | translate }}</p>
          <a
            class="prs-all-drawer__account-link"
            routerLink="/login"
            [queryParams]="{ returnUrl: '/account' }"
            rel="nofollow"
            (click)="close()"
            >{{ 'account.login.link' | translate }}</a
          >
          <a
            class="prs-all-drawer__account-link"
            routerLink="/account/request-access"
            rel="nofollow"
            (click)="close()"
            >{{ 'account.request_access.link' | translate }}</a
          >
        </div>
      </ng-template>
    </ng-container>

    <!-- Amazon-style subcategory view: icon-only back (no visible “Main Menu” label) -->
    <ng-container *ngIf="selectedCategory$ | async as selected">
      <button
        type="button"
        class="prs-all-drawer__main-menu"
        data-testing-id="prs-all-main-menu"
        [attr.aria-label]="'dias.account.cancel.link' | translate"
        (click)="clearSelection()"
      >
        <i class="bi bi-chevron-left" aria-hidden="true"></i>
      </button>

      <h2 class="prs-all-drawer__drill-title">{{ selected.name }}</h2>

      <a class="prs-all-drawer__see-all" [routerLink]="selected.url" (click)="close()">
        {{ 'common.view_all.link' | translate }} {{ selected.name }}
      </a>

      <ul class="prs-all-drawer__list">
        <li *ngFor="let subCategory of subCategories$ | async">
          <a class="prs-all-drawer__row" [routerLink]="subCategory.url" (click)="close()">{{
            subCategory.name
          }}</a>
        </li>
      </ul>
    </ng-container>
  </div>
</aside>
